import type { ListerEnrichRow } from "../../../lib/api";

/** Old against new, field by field, before anything is written — the whole reason this is a
 *  panel rather than a button. A row that is `auto` (one of the five automatic fields, changed
 *  and empty) arrives TICKED and explained; a `tick_only` row arrives CLEAR, current beside
 *  proposed, with a plain reason it is not automatic. Rows that did not change are never
 *  rendered — a list of twelve identical values would hide the three that matter, and there is
 *  no `barcode` row and no single `metafields` row: neither key exists in `EnrichKey`. */
export default function EnrichPanel({ rows, apply, onToggle, appendImages, onAppendImages, busy, onApply, onCancel, result, failure }: {
  rows: ListerEnrichRow[];
  apply: Record<string, boolean>;
  onToggle: (key: string) => void;
  appendImages: boolean;
  onAppendImages: (v: boolean) => void;
  busy: boolean;
  onApply: () => void;
  onCancel: () => void;
  result: { applied: string[]; warnings: string[]; admin_url: string } | null;
  failure: { step: string; applied: string[] } | null;
}): JSX.Element {
  // Applied — the outcome, once the writes are done. Shown in place of the rows; a fresh
  // getListerExistingDetail is what would show the rows again, not this component re-deriving them.
  if (result) {
    return (
      <div className="font-mono text-sm bg-zinc-900 border border-zinc-800 rounded-md p-3 space-y-2">
        <p className="text-green-300 text-xs">
          {result.applied.length > 0 ? `Applied: ${result.applied.join(", ")}.` : "Nothing needed changing."}
        </p>
        {result.warnings.length > 0 && (
          <ul className="text-red-400 text-xs list-disc pl-5">
            {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        )}
        <a href={result.admin_url} target="_blank" rel="noopener" className="underline text-zinc-300 text-xs">open in Shopify ↗</a>
      </div>
    );
  }

  // A duplicate hit at SCAN time has no proposal yet — research has not run, so `/existing`
  // answers with `rows: []` rather than twelve rows all reading `changed: false`. Say so
  // plainly, and offer no Apply button: there is nothing yet to compare against.
  if (rows.length === 0) {
    return (
      <div className="font-mono text-sm bg-zinc-900 border border-zinc-800 rounded-md p-3 space-y-2">
        <p className="text-zinc-500 text-xs">
          This scan has not researched the product yet; press Create anyway to research it, then enrich from the review page.
        </p>
        <button type="button" className="text-zinc-400 underline text-xs" onClick={onCancel}>Back</button>
      </div>
    );
  }

  const changed = rows.filter((r) => r.changed);
  const imagesRow = rows.find((r) => r.key === "images");

  return (
    <div className="font-mono text-sm bg-zinc-900 border border-zinc-800 rounded-md p-3 space-y-2">
      {/* THREE WRITES, NO TRANSACTION (src/web/routes/lister.ts). A 502 names the step that
          failed and lists what already landed — never "nothing happened" and never "rolled
          back", because nothing was. The rows stay live below so the operator can retry. */}
      {failure && (
        <p className="text-red-400 text-xs" role="alert">
          The {failure.step} step failed — {failure.applied.length > 0 ? `${failure.applied.join(", ")} already landed.` : "nothing landed yet."} Fix the problem and try again.
        </p>
      )}
      {changed.map((r) => (
        <label key={r.key} className="grid md:grid-cols-[auto_1fr] gap-x-3 gap-y-1 items-baseline">
          {/* data-auto mirrors the row's OWN `auto` flag, independent of the `checked` prop
              below — so a harness reading the DOM can verify "checked matches auto" instead of
              guessing which fields are auto from a fixed field-name list, which only holds while
              those fields happen to be empty on whichever product is under test. */}
          <input type="checkbox" aria-label={r.key} data-auto={r.auto ? "true" : "false"} checked={apply[r.key] ?? r.auto} disabled={busy} onChange={() => onToggle(r.key)} />
          <div className="min-w-0">
            <span className="text-zinc-300">{r.label}</span>
            {r.key === "price_cents"
              ? <p className="text-amber-300 text-xs">This product is already priced at {r.current}; tick to replace it with {r.proposed}.</p>
              : r.key === "tags"
              ? <p className="text-zinc-500 text-xs">{r.proposed} — your existing tags are kept ({r.current}).</p>
              : <p className="text-zinc-500 text-xs break-words">{r.auto ? `empty on the product — will be filled with ${r.proposed}` : `${r.current} → ${r.proposed}`}</p>}
          </div>
        </label>
      ))}
      {imagesRow?.changed && (
        <label className="flex items-center gap-2 text-zinc-300 text-xs">
          <input type="checkbox" aria-label="Append images" checked={appendImages} disabled={busy} onChange={(e) => onAppendImages(e.target.checked)} />
          Also append the scan's photographs
        </label>
      )}
      <div className="flex items-center gap-3 pt-1">
        <button type="button" className="bg-zinc-800 hover:bg-zinc-700 px-4 py-3 rounded min-w-[44px] disabled:opacity-50" disabled={busy} onClick={onApply}>{busy ? "applying…" : "Apply"}</button>
        <button type="button" className="text-zinc-400 underline" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
